import {
  isContasInstanceRootAccount,
} from "../Accounts/components/BaasEntityVirtualAccountsPanel";

const shellRole = (account) =>
  String(account?.role || account?.type || "")
    .trim()
    .toLowerCase();

/** Pinned instance shells for Root → Shells operacionais (never peer Conta Digitals). */
export const ROOT_PAGE_OPERATION_SHELL_ROLES = new Set([
  "operating",
  "overdraft_reserve",
  "overdraft",
  "program_reserve",
  "program",
]);

/**
 * Resolve this instance's RootAccountId for Shells operacionais.
 * `/programs` cards omit rootAccountId — fall back to the pinned role=root in
 * transfer-options sourceAccounts (LoadPinnedSlots emits it before linked peers).
 */
export const resolveRootPageRootAccountId = ({
  rootAccountId = null,
  sourceAccounts = [],
} = {}) => {
  const fromInstance = String(rootAccountId || "").trim();
  if (fromInstance) return fromInstance;
  const pinned = (Array.isArray(sourceAccounts) ? sourceAccounts : []).find(
    (account) => String(account?.role || "").trim().toLowerCase() === "root",
  );
  return String(pinned?.id || "").trim();
};

/**
 * Root page Shells strip: only Operating / Overdraft / Program pins + this
 * instance's RootAccountId. Never person peers, never foreign Conta Digitals
 * that still arrive as role=root from a stale Baas build, never ENTITY litter
 * promoted by name via isContasOperationShellAccount.
 *
 * Product rule: Valut matrix /baas/root stays fixed to the matrix pin's INFRA
 * shells. Peer Conta Digitals and their shells are operated from Contas detail
 * after selectInstanceForPeerOps — never mixed into this Root menu.
 */
export const isRootPageOperationShell = (account, { rootAccountId } = {}) => {
  if (!account) return false;
  if (account?.isInstanceRoot === true || account?.is_instance_root === true) {
    return false;
  }
  const role = String(account?.role || "")
    .trim()
    .toLowerCase();
  if (ROOT_PAGE_OPERATION_SHELL_ROLES.has(role)) return true;
  if (role !== "root") return false;
  const rootId = String(rootAccountId || "").trim();
  const id = String(account?.id || "").trim();
  // Without a resolved pin, do not promote destination peer roots into INFRA.
  if (!rootId || !id) return false;
  return id === rootId;
};

/** Prefer exact RootAccountId, then role=root pin, then PROGRAM_RESERVE, then first shell. */
export const pickDefaultRootShellId = (shells, { rootAccountId, preferredId } = {}) => {
  const list = Array.isArray(shells) ? shells : [];
  if (!list.length) return "";

  const preferred = String(preferredId || "").trim();
  if (preferred && list.some((s) => String(s?.id || "").trim() === preferred)) {
    return preferred;
  }

  const rootId = String(rootAccountId || "").trim();
  if (rootId) {
    const byExactId = list.find((s) => String(s?.id || "").trim() === rootId);
    if (byExactId?.id) return String(byExactId.id).trim();
  }

  const byRootPin = list.find((s) =>
    isContasInstanceRootAccount(s, { rootAccountId: rootId }),
  );
  if (byRootPin?.id) return String(byRootPin.id).trim();

  const byProgram = list.find((s) => {
    const role = shellRole(s);
    return role === "program_reserve" || role === "program";
  });
  if (byProgram?.id) return String(byProgram.id).trim();

  return String(list[0]?.id || "").trim();
};
